import React, {useCallback, useEffect} from 'react';
import {StyleSheet, View, ViewStyle, TextStyle} from 'react-native';
import {Text} from 'ui-kits';
import colors from 'theme/Colors';
import {StoryDetailsForm} from 'components/backlogs_screen';
import {spacing} from 'theme/Units';
import {NativeStackScreenProps} from '@react-navigation/native-stack';
import {RootNavigatorParams, Routes} from 'navigation/Routes';
import {userStoryFormValuesType} from 'components';
import {useDispatch, useSelector} from 'react-redux';
import {addUserStoryRequest, resetMessage} from 'redux/slices/userStorySlice';
import {getFormattedDate} from 'utils/getFormattedDate';
import {selectedProjectID} from 'redux/selectors/projectSelector';
import {
  userStoryFailure,
  userStorySuccess,
} from 'redux/selectors/userStorySelector';
import {useToast} from 'hooks';
import {ERROR_KEY, SUCCESS_KEY} from 'constants/keys';
import {
  ErrorCodes,
  getErrorMessage,
  getSuccessMessage,
  SuccessCodes,
} from 'utils/getMessages';

export function AddUserStory({
  navigation,
}: NativeStackScreenProps<RootNavigatorParams>) {
  const dispatch = useDispatch();
  const projectID = useSelector(selectedProjectID);
  const {showToasts} = useToast();
  const addUserStoryDetailsSuccess = useSelector(userStorySuccess);
  const addUserStoryDetailsError = useSelector(userStoryFailure);

  const handleCancel = useCallback(() => {
    navigation.replace(Routes.PROJECT_BACKLOGS, {
      id: projectID?.toString(),
    } as any);
  }, [navigation, projectID]);

  function handleSubmit({tags, ...values}: userStoryFormValuesType) {
    const acc: any = {};

    Object.entries(values).forEach(([key, value]) => {
      if (!value){ return;}

      switch (key) {
        case 'due_date':
          acc.due_date = getFormattedDate(value, 'yyyy-mm-dd', '-');
          break;
        case 'responsible':
          acc.responsible = value?.emp_id;
          break;
        case 'followers':
          if (value.length > 0) {
            acc.followers = value.map((item: any) => item.emp_id);
          }
          break;
        default:
          acc[key] = value;
      }
    });

    dispatch(
      addUserStoryRequest({
        ...acc,
        proj_id: projectID,
      }),
    );
  }

  useEffect(() => {
    if (
      addUserStoryDetailsSuccess &&
      addUserStoryDetailsSuccess === 'S-10020'
    ) {
      showToasts(
        SUCCESS_KEY,
        getSuccessMessage(addUserStoryDetailsSuccess as SuccessCodes),
        'success',
      );
      dispatch(resetMessage());
      handleCancel();
    }
    if (addUserStoryDetailsError) {
      showToasts(
        ERROR_KEY,
        getErrorMessage(addUserStoryDetailsError as ErrorCodes),
        'error',
      );
      dispatch(resetMessage());
    }
  }, [
    addUserStoryDetailsSuccess,
    addUserStoryDetailsError,
    dispatch,
    showToasts,
    handleCancel,
  ]);

  return (
    <View style={styles.container}>
      <Text style={styles.header}>Add User Story</Text>
      <StoryDetailsForm
        mode="add"
        onCancel={handleCancel}
        onSubmit={handleSubmit}
      />
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  header: TextStyle;
};
const styles: styleType = StyleSheet.create({
  container: {
    backgroundColor: colors.palette.white,
    flex: 1,
    padding: spacing['4'],
    gap: spacing['4'],
  },
  header: {
    fontSize: 36,
    color: colors.palette.brandBlue,
    fontWeight: '600',
  },
});
